import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HotFaceAuth } from '@hotmart-org-ca/hot-face-auth';

import type { FaceTecIDScanResult, FaceTecSessionResult } from '@/common/contexts/types';
import { useNotification } from '@/components/notification';
import { ONBOARDING_SESSION_KEY } from '@/common/constants';

export const useFaceAuth = ({ callback }: { callback: (result) => void }) => {
  const faceAuthRef = useRef<HotFaceAuth | null>(null);
  const { setError } = useNotification();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language?.toLowerCase()?.replace('_', '-');

  useEffect(() => {
    const faceAuth = new HotFaceAuth(
      sessionStorage.getItem(ONBOARDING_SESSION_KEY),
      'id_verify',
      'register_face_id_and_ocr',
      true
    );
    faceAuthRef.current = faceAuth;

    faceAuth
      .initialize((result) => {
        if (result) {
          if (currentLanguage !== 'it') {
            faceAuth.configureLocalization(currentLanguage);
          }
        } else {
          setError('try-again');
        }
      })
      .catch(() => setError('try-again'));

    return () => {
      if (faceAuthRef.current && faceAuthRef.current.cleanup) {
        faceAuthRef.current.cleanup();
      }
    };
  }, []);

  useEffect(() => {
    if (faceAuthRef.current) {
      faceAuthRef.current.setExternalCallback(
        (sessionResult: FaceTecSessionResult, idScanResult: FaceTecIDScanResult) => {
          if (callback) {
            if (sessionResult) callback(sessionResult);
            if (idScanResult) callback(idScanResult);
          }
        }
      );
    }
  }, [faceAuthRef.current]);

  const doEnrollment = () => {
    try {
      if (faceAuthRef.current) {
        faceAuthRef.current.doEnrollment();
      }
    } catch {
      setError('try-again');
    }
  };

  const doEnrollmentOcr = () => {
    try {
      if (faceAuthRef.current) {
        faceAuthRef.current.doEnrollmentOcr();
      }
    } catch {
      setError('try-again');
    }
  };

  return {
    doEnrollment,
    doEnrollmentOcr,
  };
};
